import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Save, RotateCcw, CheckCircle2 } from 'lucide-react'
import { settingsApi } from '@/services/api'

export default function SettingsPage() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['settings'], queryFn: settingsApi.list })
  const [saveMsg, setSaveMsg] = useState('')

  const initialized = data?.settings && Object.keys(data.settings).length > 0
  const settings = data?.settings?.scraper as Record<string, unknown> | undefined

  const [timeout, setTimeout_] = useState('')
  const [minDelay, setMinDelay] = useState('')
  const [maxDelay, setMaxDelay] = useState('')

  // Sync form state when data loads
  const synced = settings && timeout === '' && minDelay === ''
  if (synced) {
    setTimeout_(String(settings.timeout ?? 60))
    setMinDelay(String(settings.min_delay_ms ?? 1000))
    setMaxDelay(String(settings.max_delay_ms ?? 3000))
  }

  const initMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/v1/settings/initialize', { method: 'POST' })
      return res.json()
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  })

  const saveMutation = useMutation({
    mutationFn: async () => {
      await settingsApi.update('scraper', {
        timeout: Number(timeout) || 60,
        min_delay_ms: Number(minDelay) || 1000,
        max_delay_ms: Number(maxDelay) || 3000,
        headless: settings?.headless ?? true,
        max_retries: settings?.max_retries ?? 3,
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] })
      setSaveMsg('Настройки сохранены')
      setTimeout(() => setSaveMsg(''), 3000)
    },
  })

  return (
    <div className="fade-in" style={{ maxWidth: 640 }}>
      <div className="page-header">
        <h2>Настройки</h2>
        <p>Конфигурация поведения scraper и экспорта</p>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner" /></div>
      ) : !initialized ? (
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <div className="empty-state-icon" style={{ margin: '0 auto 20px' }}><RotateCcw size={36} /></div>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Инициализация настроек</h3>
          <p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>Создайте настройки по умолчанию для начала работы</p>
          <button className="btn btn-primary" onClick={() => initMutation.mutate()} disabled={initMutation.isPending}>
            {initMutation.isPending ? 'Инициализация...' : 'Инициализировать'}
          </button>
        </div>
      ) : (
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-md)', background: 'rgba(99,102,241,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Save size={20} style={{ color: 'var(--accent)' }} />
              </div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Scraper</div>
            </div>
            {saveMsg && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--success)' }}>
                <CheckCircle2 size={14} /> {saveMsg}
              </div>
            )}
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Таймаут (сек)</label>
              <input className="input" type="number" value={timeout} onChange={e => setTimeout_(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Headless режим</label>
              <div className="input" style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ color: settings?.headless ? 'var(--success)' : 'var(--text-muted)' }}>
                  {settings?.headless ? 'Да (без UI)' : 'Нет (с окном браузера)'}
                </span>
              </div>
            </div>
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Мин. задержка (мс)</label>
              <input className="input" type="number" value={minDelay} onChange={e => setMinDelay(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Макс. задержка (мс)</label>
              <input className="input" type="number" value={maxDelay} onChange={e => setMaxDelay(e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Макс. повторных попыток</label>
            <input className="input" type="number" value={String(settings?.max_retries ?? 3)} readOnly />
            <div className="form-hint">Количество повторных попыток при ошибках</div>
          </div>

          <button className="btn btn-primary" style={{ marginTop: 8 }} onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            <Save size={16} /> {saveMutation.isPending ? 'Сохранение...' : 'Сохранить настройки'}
          </button>

          {saveMutation.isError && (
            <div style={{ marginTop: 12, padding: 12, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-md)', color: 'var(--danger)', fontSize: 14 }}>
              Ошибка сохранения настроек
            </div>
          )}

          <div style={{ marginTop: 16, padding: 16, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.15)', borderRadius: 'var(--radius-md)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#60a5fa', marginBottom: 4 }}>ℹ️ Информация</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              Изменение настроек не влияет на уже запущенные задачи. Новые задачи будут использовать обновлённые параметры.
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
